import React, { useMemo, useState } from 'react';
import { buildPartnerGoogleCalendarUrl, formatPartnerMoney, formatPartnerScheduledAppointment } from '../utils/partnerShopApi';
import ShopReadyReportCard from './chat/ShopReadyReportCard';

function formatMessageTime(value) {
    if (!value) return '';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}

function getRequestTimelineEventCreatedAt(request = {}, eventName = '') {
    const event = Array.isArray(request?.timeline)
        ? request.timeline.find((item) => item?.event === eventName && item?.created_at)
        : null;
    return event?.created_at || '';
}

function buildAppointmentFromRequest(request = {}) {
    return {
        request_id: request?.request_id || request?.id || '',
        ticket_number: request?.ticket_number || '',
        scheduled_start_at: request?.scheduled_start_at || '',
        scheduled_end_at: request?.scheduled_end_at || '',
        scheduled_timezone: request?.scheduled_timezone || '',
        schedule_notes: request?.schedule_notes || '',
        partner_shop: request?.partner_shop || {},
        vehicle: request?.vehicle || {},
        shop_ready_summary: request?.shop_ready_summary || {},
        services: request?.services || [],
        latest_estimate: request?.latest_estimate || null,
        calendar: request?.calendar || {},
    };
}

function ConversationStatusNotice({ message }) {
    return (
        <div className="flex w-full justify-center">
            <div className="flex flex-col items-center gap-0.5">
                <span className="rounded-full border border-[#d9d9d9] bg-[#f3f3f3] px-4 py-1.5 text-[12px] font-semibold text-[#5e5e5e]">
                    {message.message_text}
                </span>
                {message.created_at && (
                    <span className="text-[10px] font-bold text-[#9d9d9d]">
                        {formatMessageTime(message.created_at)}
                    </span>
                )}
            </div>
        </div>
    );
}

// An itemized estimate rendered inline in the conversation (shown to BOTH shop and driver so
// each estimate version reads as a card, not a bare "Update" line). Older versions render muted.
function ConversationEstimateCard({ estimate, createdAt, request }) {
    const lines = Array.isArray(estimate?.lines) ? estimate.lines : [];
    const isLatest = estimate?.is_latest !== false;
    const status = String(estimate?.estimate_status || '').toLowerCase();
    const shopName = request?.partner_shop?.name || 'Shop';
    const badge = !isLatest
        ? { text: `Previous estimate${estimate?.revision ? ` (v${estimate.revision})` : ''}`, classes: 'border-[#e6e6e6] bg-[#f4f4f4] text-[#737373]' }
        : status === 'accepted'
        ? { text: 'Estimate accepted', classes: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]' }
        : status === 'declined'
        ? { text: 'Estimate declined', classes: 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]' }
        : estimate?.is_update
        ? { text: 'Estimate updated', classes: 'border-amber-200 bg-amber-50 text-amber-800' }
        : { text: 'Estimate ready', classes: 'border-teal-200 bg-white/80 text-teal-700' };
    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-600/12 text-teal-700">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9 5 3h14l2 6" />
                    <path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
                    <path d="M9 22V12h6v10" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-teal-700">{shopName}</p>
                <div className={`w-full rounded-2xl rounded-tl-sm border border-l-2 px-4 py-3 text-[#414141] shadow-sm ${
                    isLatest
                        ? 'border-teal-200/80 border-l-teal-500 bg-teal-50/70'
                        : 'border-[#e7e7e7] border-l-[#a2a2a2] bg-[#fafafa] opacity-90'
                }`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-teal-700">Itemized estimate</p>
                            <p className="mt-1 text-xl font-black text-[#414141]">{formatPartnerMoney(estimate?.total_amount, estimate?.currency)}</p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${badge.classes}`}>{badge.text}</span>
                    </div>
                    {estimate?.message && (
                        <p className="mt-3 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-[#414141]">{estimate.message}</p>
                    )}
                    {lines.length > 0 && (
                        <div className="mt-3 divide-y divide-teal-100">
                            {lines.map((line, index) => (
                                <div key={line.estimate_line_id || `${line.line_type || 'line'}-${index}`} className="flex items-start justify-between gap-3 py-2 text-sm font-bold">
                                    <span className="min-w-0 text-[#414141]">{line.description || 'Estimate line'}</span>
                                    <span className="shrink-0 text-[#414141]">{formatPartnerMoney(line.amount, estimate?.currency)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {createdAt && (
                    <p className="mt-1 text-[10px] font-bold text-teal-600">
                        {formatMessageTime(createdAt)}
                    </p>
                )}
            </div>
        </div>
    );
}

function ConversationAppointmentCard({ appointment, createdAt }) {
    const calendar = appointment?.calendar || {};
    const status = String(calendar.sync_status || '').toLowerCase();
    const badge = status === 'synced'
        ? { text: 'Google Calendar synced', classes: 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]' }
        : status === 'failed'
        ? { text: 'Calendar sync failed', classes: 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]' }
        : { text: '', classes: '' };
    const appointmentLabel = formatPartnerScheduledAppointment(appointment);
    const addToCalendarUrl = buildPartnerGoogleCalendarUrl(appointment);

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecfff5] text-[#087a43]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M16 3v4M8 3v4M3 10h18M8 15l2 2 5-5" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#087a43]">
                    {appointment?.partner_shop?.name || 'Partner shop'}
                </p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-[#bde7d5] border-l-2 border-l-[#0a8f53] bg-[#ecfff5] px-4 py-3 text-[#414141] shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#087a43]">Appointment confirmed</p>
                            <p className="mt-1 text-base font-black text-[#414141]">{appointmentLabel || 'Drop-off time scheduled'}</p>
                        </div>
                        {badge.text && (
                            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${badge.classes}`}>{badge.text}</span>
                        )}
                    </div>
                    {appointment?.partner_shop?.address && (
                        <p className="mt-2 text-sm font-semibold leading-relaxed text-[#414141]">{appointment.partner_shop.address}</p>
                    )}
                    {appointment?.schedule_notes && (
                        <p className="mt-2 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-[#414141]">{appointment.schedule_notes}</p>
                    )}
                    {(addToCalendarUrl || calendar.event_link) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {addToCalendarUrl && (
                                <a
                                    href={addToCalendarUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex min-h-[32px] items-center rounded-full border border-[#D9D9D9] bg-white px-3 text-xs font-black text-[#414141] no-underline hover:border-[#D81F26] hover:text-[#D81F26]"
                                >
                                    Add to my Google Calendar
                                </a>
                            )}
                            {calendar.event_link && (
                                <a
                                    href={calendar.event_link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex min-h-[32px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-xs font-black text-[#087a43] no-underline"
                                >
                                    Open synced event
                                </a>
                            )}
                        </div>
                    )}
                </div>
                {createdAt && (
                    <p className="mt-1 text-[10px] font-bold text-[#087a43]">
                        {formatMessageTime(createdAt)}
                    </p>
                )}
            </div>
        </div>
    );
}

function ConversationRescheduleRequestCard({ request, rescheduleRequest, createdAt }) {
    const reason = String(rescheduleRequest?.reason || '').trim();
    const appointment = {
        ...request,
        scheduled_start_at: rescheduleRequest?.previous_scheduled_start_at || request?.scheduled_start_at || '',
        scheduled_end_at: rescheduleRequest?.previous_scheduled_end_at || request?.scheduled_end_at || '',
        scheduled_timezone: rescheduleRequest?.previous_scheduled_timezone || request?.scheduled_timezone || '',
    };
    const appointmentLabel = formatPartnerScheduledAppointment(appointment);
    const shopName = request?.partner_shop?.name || 'The shop';

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff8e8] text-[#936000]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M3 21v-5h5" />
                    <path d="M3 12a9 9 0 0 1 15.74-5.74L21 8" />
                    <path d="M16 8h5V3" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#936000]">{shopName}</p>
                <div className="w-full rounded-2xl rounded-tl-sm border border-[#f7d58f] border-l-2 border-l-[#d49300] bg-[#fff8e8] px-4 py-3 text-[#414141] shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#936000]">Reschedule requested</p>
                    <p className="mt-1 text-base font-black leading-snug text-[#414141]">{shopName} is asking for a new appointment time</p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        Current appointment{appointmentLabel ? `: ${appointmentLabel}` : ' is scheduled'}.
                    </p>
                    {reason && (
                        <p className="mt-2 whitespace-pre-wrap break-words rounded-xl border border-[#f7d58f] bg-white px-3 py-2 text-sm font-semibold text-[#414141]">
                            {reason}
                        </p>
                    )}
                </div>
                {createdAt && (
                    <p className="mt-1 text-[10px] font-bold text-[#936000]">
                        {formatMessageTime(createdAt)}
                    </p>
                )}
            </div>
        </div>
    );
}

function getConversationDriverName(request = {}) {
    const displayName = String(request?.customer?.display_name || '').trim();
    if (displayName && !/^dash driver$/i.test(displayName)) return displayName;
    return 'the driver';
}

function shouldShowVisitReminder(request = {}) {
    return Boolean(request?.scheduled_start_at) && String(request?.request_status || '').toLowerCase() === 'scheduled';
}

function shouldShowServiceCompletionPrompt(request = {}) {
    return String(request?.request_status || '').toLowerCase() === 'in_service';
}

function shouldShowPickupConfirmationPrompt(request = {}) {
    return String(request?.request_status || '').toLowerCase() === 'ready_for_pickup';
}

const REQUEST_STATUS_ORDER = [
    'submitted',
    'viewed',
    'accepted',
    'estimate_sent',
    'estimate_accepted',
    'scheduled',
    'in_service',
    'ready_for_pickup',
    'picked_up',
    'completed',
];

const SERVICE_COMPLETION_CARD_STATUSES = ['in_service', 'ready_for_pickup'];
const SERVICE_STATUS_SORT_RANK = {
    scheduled: 10,
    in_service: 20,
    ready_for_pickup: 30,
    picked_up: 40,
    completed: 50,
};

function hasReachedRequestStatus(currentStatus, targetStatus) {
    const currentIndex = REQUEST_STATUS_ORDER.indexOf(String(currentStatus || '').toLowerCase());
    const targetIndex = REQUEST_STATUS_ORDER.indexOf(String(targetStatus || '').toLowerCase());
    return currentIndex >= 0 && targetIndex >= 0 && currentIndex >= targetIndex;
}

function getMessageEventCreatedAt(messages = [], eventName = '') {
    const event = Array.isArray(messages)
        ? messages.find((message) => message?.is_status_update && message?.event === eventName && message?.created_at)
        : null;
    return event?.created_at || '';
}

function getServicePromptSortTime(request = {}, messages = [], eventName = '') {
    if (eventName === 'in_service') {
        return getRequestTimelineEventCreatedAt(request, 'in_service') ||
            getMessageEventCreatedAt(messages, 'ready_for_pickup') ||
            getRequestTimelineEventCreatedAt(request, 'ready_for_pickup') ||
            getMessageEventCreatedAt(messages, 'picked_up') ||
            getRequestTimelineEventCreatedAt(request, 'picked_up') ||
            request.updated_at ||
            request.created_at ||
            request.scheduled_start_at;
    }
    if (eventName === 'ready_for_pickup') {
        return getRequestTimelineEventCreatedAt(request, 'ready_for_pickup') ||
            getMessageEventCreatedAt(messages, 'picked_up') ||
            getRequestTimelineEventCreatedAt(request, 'picked_up') ||
            request.updated_at ||
            request.created_at ||
            request.scheduled_start_at;
    }
    return request.updated_at || request.created_at || request.scheduled_start_at;
}

function getConversationSortRank(message = {}) {
    if (!message?.is_status_update) return 0;
    return SERVICE_STATUS_SORT_RANK[String(message?.event || '').toLowerCase()] || 15;
}

function ConversationVisitReminderCard({
    request,
    onStartService,
    isUpdating = false,
    actionsEnabled = true,
}) {
    if (!request?.scheduled_start_at) return null;
    const driverName = getConversationDriverName(request);
    const shopName = request?.partner_shop?.name || 'the shop';
    const appointmentLabel = formatPartnerScheduledAppointment(request);

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff8e8] text-[#936000]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2v4M16 2v4M3 10h18" />
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M9 16l2 2 4-5" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#936000]">CarFido reminder</p>
                <div className="w-full rounded-[26px] rounded-tl-sm border border-[#f7d58f] border-l-2 border-l-[#d49300] bg-[#fff8e8] px-4 py-3 text-[#414141] shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#936000]">Scheduled service</p>
                    <p className="mt-1 text-base font-black leading-snug text-[#414141]">Did {driverName} visit?</p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        {driverName === 'the driver' ? 'The driver' : driverName} had service scheduled
                        {appointmentLabel ? ` for ${appointmentLabel}` : ''} at {shopName}.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {actionsEnabled && onStartService ? (
                            <button
                                type="button"
                                onClick={onStartService}
                                disabled={isUpdating}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isUpdating ? 'Starting...' : 'Start service'}
                            </button>
                        ) : (
                            <span className="inline-flex min-h-[34px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-xs font-black text-[#087a43]">
                                Visit confirmed
                            </span>
                        )}
                        <span className="inline-flex min-h-[30px] items-center rounded-full border border-[#f7d58f] bg-white px-3 text-[11px] font-black text-[#936000]">
                            {actionsEnabled ? 'Reminder until drop-off' : 'Drop-off question answered'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConversationServiceCompletionCard({
    request,
    onMarkReadyForPickup,
    onConfirmPickup,
    isUpdating = false,
    statusOverride = '',
    actionsEnabled = true,
}) {
    const status = String(statusOverride || request?.request_status || '').toLowerCase();
    const inService = status === 'in_service';
    const readyForPickup = status === 'ready_for_pickup';
    if (!inService && !readyForPickup) return null;

    const currentStatus = String(request?.request_status || '').toLowerCase();
    const driverName = getConversationDriverName(request);
    const shopName = request?.partner_shop?.name || 'the shop';
    const driverLabel = driverName === 'the driver' ? 'the driver' : driverName;
    const canMarkReady = actionsEnabled && inService && currentStatus === 'in_service' && onMarkReadyForPickup;
    const canConfirmPickup = actionsEnabled && readyForPickup && currentStatus === 'ready_for_pickup' && onConfirmPickup;
    const passiveLabel = readyForPickup && ['picked_up', 'completed'].includes(currentStatus)
        ? 'Car taken'
        : readyForPickup
        ? 'Waiting for pickup'
        : 'Waiting for completion';

    return (
        <div className="flex w-full justify-start gap-4 md:gap-6">
            <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecfff5] text-[#087a43]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                </svg>
            </div>
            <div className="flex max-w-[85%] flex-col items-start md:max-w-[75%]">
                <p className="mb-1 ml-0.5 text-[10px] font-semibold text-[#087a43]">CarFido reminder</p>
                <div className="w-full rounded-[26px] rounded-tl-sm border border-[#bde7d5] border-l-2 border-l-[#0a8f53] bg-[#ecfff5] px-4 py-3 text-[#414141] shadow-sm">
                    <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#087a43]">
                        {readyForPickup ? 'Ready for pickup' : 'Vehicle in service'}
                    </p>
                    <p className="mt-1 text-base font-black leading-snug text-[#414141]">
                        {readyForPickup ? 'Is service complete and car taken?' : 'Is the service complete?'}
                    </p>
                    <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                        {readyForPickup
                            ? `${driverLabel === 'the driver' ? 'The driver' : driverLabel} can take the car from ${shopName}. Mark it picked up once the handoff is done.`
                            : `The vehicle is in service at ${shopName}. When the work is finished, mark it ready so ${driverLabel} can confirm the car was taken.`
                        }
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {canMarkReady && (
                            <button
                                type="button"
                                onClick={onMarkReadyForPickup}
                                disabled={isUpdating}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isUpdating ? 'Updating...' : 'Service complete'}
                            </button>
                        )}
                        {canConfirmPickup && (
                            <button
                                type="button"
                                onClick={onConfirmPickup}
                                disabled={isUpdating}
                                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-[#0a8f53] px-4 text-xs font-black text-white transition hover:bg-[#087a43] disabled:cursor-wait disabled:opacity-60"
                            >
                                {isUpdating ? 'Updating...' : 'Car taken'}
                            </button>
                        )}
                        {!canMarkReady && !canConfirmPickup && (
                            <span className="inline-flex min-h-[30px] items-center rounded-full border border-[#bde7d5] bg-white px-3 text-[11px] font-black text-[#087a43]">
                                {passiveLabel}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function shouldClampMessageText(text = '') {
    const value = String(text || '');
    const lineCount = value.split(/\r?\n/).length;
    return lineCount > 8 || value.length > 520;
}

function ExpandableMessageText({ text, mine = false, enabled = false }) {
    const [expanded, setExpanded] = useState(false);
    const shouldClamp = enabled && shouldClampMessageText(text);

    return (
        <>
            <p className={`whitespace-pre-wrap break-words ${shouldClamp && !expanded ? 'shop-chat-message-clamp' : ''}`}>{text}</p>
            {shouldClamp && (
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                    className={`mt-2 text-xs font-black ${mine ? 'text-white/80 hover:text-white' : 'text-[#D81F26] hover:text-[#B8181E]'}`}
                >
                    {expanded ? 'Show less' : 'Read more'}
                </button>
            )}
        </>
    );
}

function hasShopReadyReportAttachment(message = {}) {
    return Boolean(message?.is_initial_request) || (Array.isArray(message?.attachments) && message.attachments.some((attachment) => attachment?.type === 'shop_ready_report'));
}

export default function PartnerRequestMessages({
    messages = [],
    currentRole = 'driver',
    title = 'Messages',
    emptyText = 'No messages yet.',
    placeholder = 'Write a message...',
    onSend,
    disabled = false,
    showMessageList = true,
    variant = 'panel',
    request = null,
    onStartService,
    onMarkReadyForPickup,
    onConfirmPickup,
    isUpdatingVisit = false,
    isUpdatingService = false,
}) {
    const [draft, setDraft] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const isChatVariant = variant === 'chat';
    const currentRequestStatus = String(request?.request_status || '').toLowerCase();
    const showVisitReminder = isChatVariant && shouldShowVisitReminder(request);
    const showServiceCompletionPrompt = isChatVariant && shouldShowServiceCompletionPrompt(request);
    const showPickupConfirmationPrompt = isChatVariant && shouldShowPickupConfirmationPrompt(request);
    const showInlinePrompt = showVisitReminder || showServiceCompletionPrompt || showPickupConfirmationPrompt;
    const sortedMessages = useMemo(() => {
        const sourceMessages = Array.isArray(messages) ? messages : [];
        const sourceStatusEvents = new Set(sourceMessages
            .filter((message) => message?.is_status_update)
            .map((message) => String(message?.event || '').toLowerCase()));
        const hasScheduledAppointment = Boolean(request?.scheduled_start_at);
        const hasAppointmentMessage = sourceMessages.some((message) => (
            message?.is_status_update && message?.event === 'scheduled' && message?.appointment
        ));
        const fallbackAppointmentMessage = hasScheduledAppointment && !hasAppointmentMessage
            ? {
                message_id: `appointment-${request?.request_id || request?.id || 'request'}`,
                request_id: request?.request_id || request?.id || '',
                sender_role: 'system',
                sender_label: 'Update',
                message_text: 'Appointment confirmed',
                created_at: getRequestTimelineEventCreatedAt(request, 'scheduled') ||
                    request.created_at ||
                    request.updated_at ||
                    request.scheduled_start_at,
                is_status_update: true,
                event: 'scheduled',
                appointment: buildAppointmentFromRequest(request),
                is_fallback_appointment: true,
            }
            : null;
        const syntheticServiceMessages = SERVICE_COMPLETION_CARD_STATUSES
            .filter((eventStatus) => (
                isChatVariant &&
                hasReachedRequestStatus(currentRequestStatus, eventStatus) &&
                !sourceStatusEvents.has(eventStatus)
            ))
            .map((eventStatus) => ({
                message_id: `service-card-${request?.request_id || request?.id || 'request'}-${eventStatus}`,
                request_id: request?.request_id || request?.id || '',
                sender_role: 'system',
                sender_label: 'Update',
                message_text: eventStatus === 'ready_for_pickup' ? 'Vehicle is ready for pickup' : 'Vehicle is in service',
                created_at: getServicePromptSortTime(request, sourceMessages, eventStatus),
                is_status_update: true,
                event: eventStatus,
                is_synthetic_service_prompt: true,
            }));
        return [
            ...sourceMessages,
            ...(fallbackAppointmentMessage ? [fallbackAppointmentMessage] : []),
            ...syntheticServiceMessages,
        ].sort((a, b) => {
            const aTime = new Date(a?.created_at || 0).getTime();
            const bTime = new Date(b?.created_at || 0).getTime();
            if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return aTime - bTime;
            if (Number.isFinite(aTime) !== Number.isFinite(bTime)) return Number.isFinite(aTime) ? -1 : 1;
            const rankDelta = getConversationSortRank(a) - getConversationSortRank(b);
            if (rankDelta) return rankDelta;
            return Number(a?.message_id || 0) - Number(b?.message_id || 0);
        });
    }, [currentRequestStatus, isChatVariant, messages, request]);
    const hasScheduledCardInTimeline = sortedMessages.some((message) => (
        message?.is_status_update && message?.event === 'scheduled' && message?.appointment
    ));
    const hasSyntheticServicePrompt = sortedMessages.some((message) => message?.is_synthetic_service_prompt);
    const hasAnyInlinePrompt = showInlinePrompt || hasSyntheticServicePrompt;

    const submitMessage = async (event) => {
        event.preventDefault();
        const message = draft.trim();
        if (!message || !onSend || disabled || isSending) return;
        setIsSending(true);
        setError('');
        try {
            await onSend(message);
            setDraft('');
        } catch (err) {
            setError(err?.message || 'Could not send message.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className={isChatVariant ? 'space-y-5' : 'rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm'}>
            {!isChatVariant && (
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <p className="text-[12px] font-black uppercase tracking-[0.12em] text-[#414141]">{title}</p>
                        <p className="mt-1 text-xs font-medium text-[#414141]">Driver and shop conversation</p>
                    </div>
                    <span className="rounded-full bg-[#f5f5f5] px-2.5 py-1 text-[10px] font-black text-[#414141]">
                        {sortedMessages.length}
                    </span>
                </div>
            )}

            {showMessageList && (
                <div className={isChatVariant ? 'space-y-6' : 'mt-4 space-y-4'}>
                    {sortedMessages.length ? sortedMessages.map((message) => {
                        // Estimate versions render as itemized cards (each version its own card),
                        // not as plain "Update" text bubbles.
                        if (message.is_status_update && message.event === 'estimate_sent' && message.estimate) {
                            return (
                                <React.Fragment key={message.message_id || `estimate-${message.created_at}`}>
                                    <ConversationEstimateCard
                                        estimate={message.estimate}
                                        createdAt={message.created_at}
                                        request={request}
                                    />
                                    <ConversationStatusNotice message={message} />
                                </React.Fragment>
                            );
                        }
                        if (message.is_status_update && message.event === 'scheduled' && message.appointment) {
                            return (
                                <React.Fragment key={message.message_id || `appointment-${message.created_at}`}>
                                    <ConversationAppointmentCard
                                        appointment={message.appointment}
                                        createdAt={message.created_at}
                                    />
                                    {!message.is_fallback_appointment && <ConversationStatusNotice message={message} />}
                                    <ConversationVisitReminderCard
                                        request={request}
                                        onStartService={onStartService}
                                        isUpdating={isUpdatingVisit}
                                        actionsEnabled={currentRequestStatus === 'scheduled'}
                                    />
                                </React.Fragment>
                            );
                        }
                        if (message.is_status_update && message.event === 'reschedule_requested' && message.reschedule_request) {
                            return (
                                <ConversationRescheduleRequestCard
                                    key={message.message_id || `reschedule-${message.created_at}`}
                                    request={request}
                                    rescheduleRequest={message.reschedule_request}
                                    createdAt={message.created_at}
                                />
                            );
                        }
                        if (message.is_status_update && ['in_service', 'ready_for_pickup'].includes(String(message.event || '').toLowerCase())) {
                            const eventStatus = String(message.event || '').toLowerCase();
                            return (
                                <React.Fragment key={message.message_id || `service-${message.event}-${message.created_at}`}>
                                    <ConversationServiceCompletionCard
                                        request={request}
                                        statusOverride={eventStatus}
                                        actionsEnabled={eventStatus === currentRequestStatus}
                                        onMarkReadyForPickup={onMarkReadyForPickup}
                                        onConfirmPickup={onConfirmPickup}
                                        isUpdating={isUpdatingService}
                                    />
                                    {!message.is_synthetic_service_prompt && <ConversationStatusNotice message={message} />}
                                </React.Fragment>
                            );
                        }
                        if (message.is_status_update) {
                            return (
                                <ConversationStatusNotice
                                    key={message.message_id || `status-${message.event}-${message.created_at}`}
                                    message={message}
                                />
                            );
                        }
                        const mine = message.sender_role === currentRole;
                        const senderLabel = message.sender_label || (message.sender_role === 'shop' ? 'Shop' : 'Driver');
                        return (
                            <div
                                key={message.message_id || `${message.sender_role}-${message.created_at}`}
                                className={`flex w-full group ${mine ? 'flex-col items-end' : 'justify-start'} ${isChatVariant ? 'animate-in fade-in duration-500' : ''}`}
                            >
                                {!mine && (
                                    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f6f6f6] text-[#414141] md:h-9 md:w-9">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="8" r="4" />
                                            <path d="M4 21a8 8 0 0 1 16 0" />
                                        </svg>
                                    </div>
                                )}
                                <div className={`flex max-w-[85%] flex-col ${mine ? 'items-end' : 'items-start'} ${isChatVariant ? 'md:max-w-[68%]' : 'md:max-w-[75%]'} ${!mine ? 'ml-4 md:ml-6' : ''}`}>
                                    {!mine && (
                                        <p className="mb-1 ml-0.5 text-[10px] font-semibold text-black/40">{senderLabel}</p>
                                    )}
                                    <div className={`rounded-[26px] px-5 py-3 text-[15px] font-medium leading-relaxed shadow-sm md:px-5 md:py-3.5 md:text-base ${
                                        mine
                                            ? 'rounded-tr-sm bg-[#414141] text-white'
                                            : 'rounded-tl-sm bg-neutral-100 text-black'
                                    }`}>
                                        <ExpandableMessageText
                                            text={message.message_text}
                                            mine={mine}
                                            enabled={isChatVariant}
                                        />
                                    </div>
                                    {hasShopReadyReportAttachment(message) && (
                                        <ShopReadyReportCard
                                            request={request}
                                            className="mt-2"
                                        />
                                    )}
                                    {message.created_at && (
                                        <p className={`mt-1 text-[11px] font-semibold ${mine ? 'mr-1 text-black/35' : 'ml-1 text-black/35'}`}>
                                            {formatMessageTime(message.created_at)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    }) : hasAnyInlinePrompt ? null : (
                        <div className="rounded-2xl border border-dashed border-[#d7d7d7] bg-[#F9F9F9] px-4 py-5 text-center text-sm font-bold text-[#414141]">
                            {emptyText}
                        </div>
                    )}
                </div>
            )}

            {showVisitReminder && !hasScheduledCardInTimeline && (
                <ConversationVisitReminderCard
                    request={request}
                    onStartService={onStartService}
                    isUpdating={isUpdatingVisit}
                />
            )}

            <form
                onSubmit={submitMessage}
                className={isChatVariant
                    ? `${showMessageList ? 'mt-6' : 'mt-3'} rounded-[28px] border border-[#d9d9d9] bg-white p-2 shadow-sm`
                    : `${showMessageList ? 'mt-4' : 'mt-3'} space-y-2`
                }
            >
                <textarea
                    value={draft}
                    onChange={(event) => {
                        setDraft(event.target.value.slice(0, 2000));
                        setError('');
                    }}
                    placeholder={placeholder}
                    disabled={disabled || isSending}
                    rows={3}
                    className={isChatVariant
                        ? 'max-h-40 min-h-[84px] w-full resize-y rounded-[22px] border-0 bg-transparent px-4 py-3 text-[15px] font-medium leading-relaxed text-[#414141] outline-none placeholder:text-[#9e9e9e] disabled:cursor-not-allowed disabled:opacity-60'
                        : 'w-full resize-none rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-3 py-3 text-sm font-semibold text-[#414141] outline-none transition focus:border-[#D81F26] disabled:cursor-not-allowed disabled:opacity-60'
                    }
                />
                {error && <p className="text-xs font-bold text-[#b42318]">{error}</p>}
                <div className={isChatVariant ? 'flex items-center justify-between gap-3 border-t border-[#d9d9d9] px-2 pb-1 pt-2' : 'flex items-center justify-between gap-3'}>
                    <span className="text-[10px] font-bold text-[#9e9e9e]">{draft.length}/2000</span>
                    <button
                        type="submit"
                        disabled={disabled || isSending || !draft.trim()}
                        className={isChatVariant
                            ? 'inline-flex h-9 items-center justify-center rounded-full bg-[#D81F26] px-4 text-xs font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60'
                            : 'inline-flex h-9 items-center justify-center rounded-xl bg-[#D81F26] px-4 text-xs font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60'
                        }
                    >
                        {isSending ? 'Sending' : 'Send'}
                    </button>
                </div>
            </form>
        </div>
    );
}
